import { Injectable } from "@angular/core";
import {
  addDoc,
  collection,
  collectionData,
  doc,
  DocumentData,
  Firestore,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  QueryDocumentSnapshot,
  startAfter,
  startAt,
  updateDoc,
  where,
} from "@angular/fire/firestore";
import { from, map, Observable } from "rxjs";

export interface CompanyGeolocation {
  lat: number;
  lng: number;
  accuracy: number;
}

@Injectable({
  providedIn: "root",
})
export class CompanyService {
  private itemsPerPage = 10;
  private lastDoc: QueryDocumentSnapshot<DocumentData> | null = null;
  private firstDoc: QueryDocumentSnapshot<DocumentData> | null = null;
  private lastVisibleDocs: QueryDocumentSnapshot<DocumentData>[] = [];
  hasNextPage = false;
  hasPreviousPage = false;

  private collectionRef;
  private collectionName = "companies";
  private fieldSort = "nomeFantasia";

  constructor(private firestore: Firestore) {
    this.collectionRef = collection(this.firestore, this.collectionName);
  }

  /** Normaliza geolocalização (Map ou GeoPoint do Firestore). */
  normalizeGeolocation(geo: any): CompanyGeolocation {
    if (!geo) {
      return { lat: 0, lng: 0, accuracy: 0 };
    }
    if (typeof geo.latitude === "number" || typeof geo.longitude === "number") {
      return {
        lat: Number(geo.latitude) || 0,
        lng: Number(geo.longitude) || 0,
        accuracy: Number(geo.accuracy) || 0,
      };
    }
    return {
      lat: Number(geo.lat) || 0,
      lng: Number(geo.lng) || 0,
      accuracy: Number(geo.accuracy) || 0,
    };
  }

  /** Mesma regra do mobile: visível se tem geo válida OU categoria. */
  isVisibleInApp(company: any): boolean {
    const geo = this.normalizeGeolocation(company?.geolocalizacao);
    const hasGeo = geo.lat !== 0 || geo.lng !== 0;
    const hasCategory = !!String(company?.categoria ?? "").trim();
    return hasGeo || hasCategory;
  }

  private mapCompanyDoc(
    docSnap: QueryDocumentSnapshot<DocumentData> | { id: string; data: () => DocumentData }
  ): any {
    const raw = docSnap.data();
    const geolocalizacao = this.normalizeGeolocation(raw["geolocalizacao"]);
    return {
      id: docSnap.id,
      ...raw,
      geolocalizacao,
      cashbackPercentual:
        typeof raw["cashbackPercentual"] === "number"
          ? raw["cashbackPercentual"]
          : 5,
      limiteCompra:
        typeof raw["limiteCompra"] === "number" ? raw["limiteCompra"] : 200,
      status: typeof raw["status"] === "number" ? raw["status"] : 0,
      isOnline: raw["isOnline"] === true,
      telefones: Array.isArray(raw["telefones"]) ? raw["telefones"] : [],
    };
  }

  async create(company: any) {
    const payload = {
      ...company,
      cashbackPercentual: Number(company.cashbackPercentual) || 5,
      limiteCompra: Number(company.limiteCompra) || 200,
      status: company.status ?? 1,
      email: (company.email ?? "").trim().toLowerCase(),
    };
    const docRef = await addDoc(this.collectionRef, payload);
    return await updateDoc(docRef, { id: docRef.id });
  }

  async update(id: string, company: any) {
    const docRef = doc(this.firestore, this.collectionName, id);
    const payload = { ...company };
    if (payload.email != null) {
      payload.email = String(payload.email).trim().toLowerCase();
    }
    if (payload.cashbackPercentual != null) {
      payload.cashbackPercentual = Number(payload.cashbackPercentual);
    }
    if (payload.limiteCompra != null) {
      payload.limiteCompra = Number(payload.limiteCompra);
    }
    return await updateDoc(docRef, payload);
  }

  async updateStatus(id: string, status: number) {
    const docRef = doc(this.firestore, this.collectionName, id);
    return await updateDoc(docRef, { status });
  }

  async updatePhoto(id: string, url: any) {
    const docRef = doc(this.firestore, this.collectionName, id);
    return await updateDoc(docRef, {
      foto: url,
    });
  }

  async getCompanyByCNPJ(cnpj: string): Promise<boolean> {
    const collectionRef = collection(this.firestore, this.collectionName);
    const q = query(collectionRef, where("cnpj", "==", cnpj));
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  }

  async getCompanyById(id: string): Promise<any> {
    const docRef = doc(this.firestore, this.collectionName, id);
    const snapshot = await getDoc(docRef);
    if (!snapshot.exists()) {
      return null;
    }
    return this.mapCompanyDoc(snapshot as QueryDocumentSnapshot<DocumentData>);
  }

  getCompanies(): Observable<any[]> {
    return collectionData(this.collectionRef, { idField: "id" }) as Observable<
      any[]
    >;
  }

  getFirstPage(): Observable<DocumentData[]> {
    const colRef = collection(this.firestore, this.collectionName);
    const q = query(
      colRef,
      orderBy(this.fieldSort, "asc"),
      limit(this.itemsPerPage)
    );

    return from(getDocs(q)).pipe(
      map((snapshot) => {
        if (!snapshot.empty) {
          this.firstDoc = snapshot.docs[0];
          this.lastDoc = snapshot.docs[snapshot.docs.length - 1];
          this.lastVisibleDocs = [this.firstDoc];
          this.hasPreviousPage = false;
          this.hasNextPage = snapshot.docs.length === this.itemsPerPage;
        } else {
          this.hasNextPage = false;
          this.hasPreviousPage = false;
        }
        return snapshot.docs.map((d) => this.mapCompanyDoc(d));
      })
    );
  }

  getNextPage(): Observable<DocumentData[]> {
    if (!this.lastDoc) return new Observable();

    const colRef = collection(this.firestore, this.collectionName);
    const q = query(
      colRef,
      orderBy(this.fieldSort, "asc"),
      startAfter(this.lastDoc),
      limit(this.itemsPerPage)
    );

    return from(getDocs(q)).pipe(
      map((snapshot) => {
        if (!snapshot.empty) {
          this.firstDoc = snapshot.docs[0];
          this.lastDoc = snapshot.docs[snapshot.docs.length - 1];
          this.lastVisibleDocs.push(this.firstDoc);
          this.hasPreviousPage = true;
          this.hasNextPage = snapshot.docs.length === this.itemsPerPage;
        } else {
          this.hasNextPage = false;
        }
        return snapshot.docs.map((d) => this.mapCompanyDoc(d));
      })
    );
  }

  getPreviousPage(): Observable<DocumentData[]> {
    if (this.lastVisibleDocs.length < 2) return new Observable();

    const previousDoc = this.lastVisibleDocs[this.lastVisibleDocs.length - 2];
    this.lastVisibleDocs.pop();

    const colRef = collection(this.firestore, this.collectionName);
    const q = query(
      colRef,
      orderBy(this.fieldSort, "asc"),
      startAt(previousDoc),
      limit(this.itemsPerPage)
    );

    return from(getDocs(q)).pipe(
      map((snapshot) => {
        if (!snapshot.empty) {
          this.firstDoc = snapshot.docs[0];
          this.lastDoc = snapshot.docs[snapshot.docs.length - 1];
          this.hasPreviousPage = this.lastVisibleDocs.length > 1;
          this.hasNextPage = true;
        }
        return snapshot.docs.map((d) => this.mapCompanyDoc(d));
      })
    );
  }
}
